import { Box } from '@components/ui/atoms/Box';
import { Flex } from '@components/ui/atoms/Flex';
import { Text } from '@components/ui/atoms/Text';
import { Avatar } from '@components/ui/molecules/Avatar';
import {
  CardPos,
  DocumentText,
  MoneyRecive,
  Notification as NotificationIcon,
} from 'iconsax-reactjs';

import type { Notification } from '../../NotificationModal/types';

interface NotificationItemProps {
  notification: Notification;
  isSelected: boolean;
  onClick: () => void;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  isSelected,
  onClick,
}) => {
  const getNotificationIcon = () => {
    if (notification.type === 'system' || notification.type === 'dispute') {
      return (
        <Flex
          w="40px"
          h="40px"
          borderRadius="full"
          bg="blue.400"
          alignItems="center"
          justifyContent="center"
          position="relative"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 8V13"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M11.9945 16H12.0035"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Flex>
      );
    }

    if (notification.avatar) {
      return <Avatar src={notification.avatar} name={notification.title} w="40px" h="40px" />;
    }

    return (
      <Flex
        w="40px"
        h="40px"
        borderRadius="full"
        bg="blue.500"
        alignItems="center"
        justifyContent="center"
        color="white"
        fontWeight="600"
        fontSize="14px"
      >
        {notification.initials}
      </Flex>
    );
  };

  const getBadgeIcon = () => {
    if (notification.type === 'invoice') {
      return <DocumentText size={12} variant="Linear" color="#FFF" />;
    }

    if (notification.type === 'payment') {
      return <CardPos size={12} variant="Linear" color="#FFF" />;
    }

    if (notification.type === 'refund') {
      return <MoneyRecive size={12} variant="Linear" color="#FFF" />;
    }

    if (notification.type === 'system') {
      return <NotificationIcon size={12} variant="Linear" color="#FFF" />;
    }

    return null;
  };

  const getBadgeColor = () => {
    switch (notification.type) {
      case 'invoice':
        return '#FF6B6B';
      case 'payment':
        return '#4CAF50';
      case 'refund':
        return '#2196F3';
      case 'system':
      case 'dispute':
        return '#9C27B0';
      default:
        return '#FF6B6B';
    }
  };

  return (
    <Flex
      p="16px 20px"
      gap="12px"
      alignItems="flex-start"
      cursor="pointer"
      bg={isSelected ? 'brand.50' : 'transparent'}
      borderLeft="3px solid"
      borderColor={isSelected ? 'brand' : 'transparent'}
      _hover={{ bg: 'gray.50' }}
      onClick={onClick}
      position="relative"
    >
      {/* Unread indicator dot */}
      {!notification.isRead && (
        <Box
          position="absolute"
          right="20px"
          top="50%"
          transform="translateY(-50%)"
          w="8px"
          h="8px"
          borderRadius="full"
          bg="red.500"
        />
      )}

      {/* Avatar with badge */}
      <Box position="relative" flexShrink="0">
        {getNotificationIcon()}
        <Flex
          alignItems="center"
          justifyContent="center"
          position="absolute"
          bottom="-2px"
          right="-2px"
          w="20px"
          h="20px"
          borderRadius="full"
          bg={getBadgeColor()}
          p="0"
        >
          {getBadgeIcon()}
        </Flex>
      </Box>

      {/* Content */}
      <Flex direction="column" flex="1" gap="4px" minW="0">
        <Text fontWeight="600" fontSize="14px" color="title.dark" lineHeight="1.2" truncate>
          {notification.title}
        </Text>
        <Text
          fontWeight="400"
          fontSize="13px"
          color="gray.600"
          lineHeight="1.4"
          lineClamp={2}
          wordBreak="break-word"
        >
          {notification.message}{' '}
          {notification.amount && (
            <Text as="span" fontWeight="600" color="title.dark">
              {notification.amount}
            </Text>
          )}
        </Text>
        <Text fontWeight="400" fontSize="12px" color="gray.400">
          {notification.timeAgo}
        </Text>
      </Flex>
    </Flex>
  );
};
